"use strict";

/*
* @ngDoc          Service
* @name           nms.management.PermissionsValidatorService
* @description    Validador para permissões de usuários e grupos.
* @requires       [$rootScope, NMS_FEATURES]
*/
var app = angular.module("nms.management");
app.service("PermissionsValidatorService", [
    "$rootScope",
    "NMS_FEATURES",
    "$translate",
    function ($rootScope, NMS_FEATURES, $translate) {
        const service: any = {};

        const findFeature = function (features, searchFeature) {
            return _.find(features, function (feature) {
                return feature.name === searchFeature;
            });
        };

        const showErrorMessage = function (messageKey) {
            $rootScope.showDialog({
                translateKey: messageKey
            });
        };

        /**
         * Valida as permissões que são dependentes.
         * Em dependentFeaturesRules foram adicionadas as featuresRequireds,
         * ou seja features nas quais possuem dependentes.
         * Para cada feature dependente, ele verifica se na listagem selecionada ela foi informada.
         * Caso negativo o sistema irá verificar se foi apenas uma ou múltiplas
         * features que foram selecionadas e fará o tratamento correto para concatenação e exibição da mensagem
         * @param selectedFeatures features selecionadas pelo usuário
         * @param dependentFeaturesRules features que possuem dependentes
         * @returns true ou false.
         */
        function validateDependentFeatures(selectedFeatures, dependentFeaturesRules): boolean {
            for (const [requiredFeature, groupRules] of dependentFeaturesRules.entries()) {
                const hasRequiredFeature = findFeature(selectedFeatures, requiredFeature);

                if (!hasRequiredFeature) {
                    const invalidFeatures = groupRules.filter(({ feature }) => findFeature(selectedFeatures, feature));

                    if (invalidFeatures.length === 1) {
                        const featureKey = `manage.permissions.features.${invalidFeatures[0].feature}.validate`;
                        showErrorMessage(featureKey);
                        return false;
                    } else if (invalidFeatures.length > 1) {
                        processMultipleFeatures(invalidFeatures.map(violation => violation.feature), requiredFeature);
                        return false;
                    }
                }
            }
            return true;
        }

        /**
         * Concatena as permissões dependentes em uma única mensagem
         * @param invalidFeatures features que dependem de requiredFeature
         * @param requiredFeature feature obrigatória para que outras funcionalidades possam ser marcadas/desmarcadas
         */
        function processMultipleFeatures(invalidFeatures: string[], requiredFeature: string) {
            const requiredFeatureTranslated = $translate.instant(`manage.permissions.features.name.${requiredFeature}`);
            const invalidFeaturesTranslated = invalidFeatures
                .map(feature => $translate.instant(`manage.permissions.features.name.${feature}`))
                .join($translate.instant("manage.permissions.features.name.concat"));

            const options: any = {
                translateKey: "manage.permissions.feature.multiples.validate",
                params: [invalidFeaturesTranslated, requiredFeatureTranslated],
                paramsInsideMessage: true
            };
            $rootScope.showDialog(options);
        }


        /**
         * Valida as permissões de features.
         *
         * Regras de validação:
         *     - A permissão 'templateConfig' só pode ser habilitada se a permissão 'template' também estiver habilitada.
         *     - A permissão 'removeLinkFromNms' ou 'linkConfiguration' só pode ser configurada se a permissão 'links' também
         * for habilitada.
         *     - A permissão 'templateInstanceRemove' ou 'templateInstanceRemoveNms' só pode ser configurada se a permissão
         *  'templateInstance' estiver habilitada.
         *
         * As features que possuem dependência foram adicionadas em um mapa, cuja chave é a permissão requerida, e os valores
         * são as features dependentes.
         */
        const validateFeatures = function (featuresData) {
            const selectedFeatures = featuresData.selection.checked;
            const templateInstanceFeatures = [
                {
                    feature: NMS_FEATURES.templateInstanceRemove.feature,
                    errorMessageKey: "manage.permissions.features.templateInstanceRemove.validate"
                },
                {
                    feature: NMS_FEATURES.templateInstanceRemoveNms.feature,
                    errorMessageKey: "manage.permissions.features.templateInstanceRemoveNms.validate"
                }
            ];
            const templateFeatures = [
                {
                    feature: NMS_FEATURES.templateConfig.feature,
                    errorMessageKey: "manage.permissions.features.templateConfig.validate"
                }
            ];

            const linkFeatures = [
                {
                    feature: NMS_FEATURES.linkConfiguration.feature,
                    errorMessageKey: "manage.permissions.features.linkConfiguration.validate"
                },
                {
                    feature: NMS_FEATURES.removeLinkFromNms.feature,
                    errorMessageKey: "manage.permissions.features.removeLinkFromNms.validate"
                }
            ];

            const dwdmCircuitFeatures = [
                {
                    feature: NMS_FEATURES.dwdmCircuitConfiguration.feature,
                    errorMessageKey: "manage.permissions.features.dwdmCircuitConfiguration.validate"
                },
                {
                    feature: NMS_FEATURES.removeDwdmCircuitFromNms.feature,
                    errorMessageKey: "manage.permissions.features.removeDwdmCircuitFromNms.validate"
                },
                {
                    feature: NMS_FEATURES.removeDwdmCircuit.feature,
                    errorMessageKey: "manage.permissions.features.removeDwdmCircuit.validate"
                }
            ];

            const dwdmMuxDemuxFeatures = [
                {
                    feature: NMS_FEATURES.dwdmMuxDemuxConfiguration.feature,
                    errorMessageKey: "manage.permissions.features.dwdmMuxDemuxConfiguration.validate"
                },
                {
                    feature: NMS_FEATURES.removeDwdmMuxDemux.feature,
                    errorMessageKey: "manage.permissions.features.removeDwdmMuxDemux.validate"
                }
            ];

            const dependentFeaturesRules: Map<string, { feature: string; errorMessageKey: string }[]> = new Map();
            dependentFeaturesRules.set(NMS_FEATURES.templateInstance.feature, templateInstanceFeatures);
            dependentFeaturesRules.set(NMS_FEATURES.template.feature, templateFeatures);
            dependentFeaturesRules.set(NMS_FEATURES.links.feature, linkFeatures);
            dependentFeaturesRules.set(NMS_FEATURES.dwdmCircuits.feature, dwdmCircuitFeatures);
            dependentFeaturesRules.set(NMS_FEATURES.dwdmMuxDemux.feature, dwdmMuxDemuxFeatures);

            return validateDependentFeatures(selectedFeatures, dependentFeaturesRules);
        };

        const hasFeature = function (features, featureName) {
            return _.some(features, { name: featureName });
        };

        /**
         * Verifica se nenhuma regra de restrição está vazia.
         */
        const hasTemplateRuleTypeEmpty = function (templatesData) {
            return _.some(templatesData.restrictions.rules, function (rule) {
                return _.isEmpty(rule.values) || _.includes(rule.values, "", null, undefined);
            });
        };

        /**
         * Verifica se contêm a feature 'templateConfig'.
         */
        const hasTemplateConfigFeature = function (featuresData) {
            return hasFeature(featuresData, NMS_FEATURES.templateConfig.feature);
        };

        /**
         * Verifica se não contêm a feature 'templateInstance'.
         */
        const notContainsTemplateInstanceFeature = function (featuresData) {
            return !hasFeature(featuresData, NMS_FEATURES.templateInstance.feature);
        };

        /**
         * Valida as permissões de templates.
         *
         * Regras de validação:
         *     - Não são permitidas restrições vazias.
         *     - Não é permitido configurar restrições de template se a feature 'templateConfig' estiver habilitada.
         *     - Não é permitido configurar restrições de template se a feature 'templateInstance' estiver desabilitada.
         */
        const validateTemplateRestrictions = function (templatesData, featuresData) {
            if (templatesData.restrictions.useRules) {
                if (hasTemplateRuleTypeEmpty(templatesData)) {
                    showErrorMessage("manage.permissions.templates.empty.restrictions");
                    return false;
                } else if (hasTemplateConfigFeature(featuresData.selection.checked)) {
                    showErrorMessage("manage.permissions.templates.templateConfig.permission.validate");
                    return false;
                } else if (notContainsTemplateInstanceFeature(featuresData.selection.checked)) {
                    showErrorMessage("manage.permissions.templates.restrictions.without.permisison");
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        };

        /*
         * @ngDoc  method
         * @name   nms.management.PermissionsValidatorService#validate
         * @param  {form}  Objeto que contem os dados de permissões.
         * @return {boolean} True se as permissões são válidas, false caso contrário.
         */
        service.validate = function (form) {
            return validateFeatures(form.features.data) && validateTemplateRestrictions(form.templates.data, form.features.data);
        };

        /*
         * Verifica se o checkbox de restrição de templates está desmarcado, porém contém critérios de restrição associados
         * @return {boolean} True se a condição for verdadeira, false caso contrário.
         */
        service.isCheckDisabledAndRestrictionsNotEmpty = function (templatesData) {
            return (
                templatesData &&
                templatesData.restrictions &&
                !templatesData.restrictions.useRules &&
                templatesData.restrictions.rules &&
                !hasTemplateRuleTypeEmpty(templatesData)
            );
        };

        return service;
    }
]);
